import type { AnimationEvent, PropsWithChildren, ReactNode } from "react";
import { useCallback } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Box } from "@similarweb/ui-components/foundations";

import { ExpandedFiltersOverlay } from "app/pages/sales-intelligence/common-components/filters-panel/components/ExpandedFiltersOverlay/ExpandedFiltersOverlay";
import { OverlayHeader } from "app/pages/sales-intelligence/common-components/filters-panel/components/ExpandedFiltersOverlay/OverlayHeader";
import { OverlayScrollableContent } from "app/pages/sales-intelligence/common-components/filters-panel/components/ExpandedFiltersOverlay/OverlayScrollableContent";
import OverlayTitleGroup from "app/pages/sales-intelligence/common-components/filters-panel/components/ExpandedFiltersOverlay/OverlayTitleGroup";
import { FilterCategoryChipList } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterCategoryChipList/FilterCategoryChipList";
import { DeferredFilterContent } from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersList/DeferredFilterContent";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { SearchResultsGuard } from "../../components/SearchResultsGuard/SearchResultsGuard";
import { useFilterCategoryPanelState } from "../../hooks/useFilterCategoryPanelState";
import { useFilterSearchTracking } from "../../hooks/useFilterSearchTracking";
import type { IFilterManagerModel } from "../../services/filters-manager/types";
import { setSearchQuery } from "../../store/action-creators";
import type { IFilterCategoryWithCount } from "../../types/category";
import advancedFilteringI18nKeys from "../../utils/i18n";

type Props<T extends IPlainObject = IPlainObject> = PropsWithChildren<{
    filtersManager: IFilterManagerModel<T>;
    categoriesWithCounts: readonly IFilterCategoryWithCount[];
    isOpen: boolean;
    skipEntryAnimation: boolean;
    transformOrigin?: string;
    isLoading?: boolean;
    onClose: VoidFunction;
    onAnimationEnd: (event: AnimationEvent<HTMLElement>) => void;
    belowSearchSlot?: ReactNode;
}>;

export const CategoryOverlayContainer = <T extends IPlainObject = IPlainObject>({
    filtersManager,
    categoriesWithCounts,
    isOpen,
    skipEntryAnimation,
    transformOrigin,
    isLoading,
    onClose,
    onAnimationEnd,
    belowSearchSlot,
    children,
}: Props<T>) => {
    const { translate } = useTranslate();

    const dispatch = useDispatch();

    const { selectedCategory, onChangeCategory } = useFilterCategoryPanelState();

    const { onTrackSearchFocus, onTrackSearchChange } = useFilterSearchTracking(filtersManager);

    const handleSearchChange = useCallback(
        (query: string) => {
            onTrackSearchChange(query);
            dispatch(setSearchQuery(query));
        },
        [onTrackSearchChange],
    );

    return (
        <ExpandedFiltersOverlay
            isOpen={isOpen}
            skipEntryAnimation={skipEntryAnimation}
            transformOrigin={transformOrigin}
            onAnimationEnd={onAnimationEnd}
        >
            <OverlayHeader
                onSearchChange={handleSearchChange}
                onSearchFocus={onTrackSearchFocus}
                onClose={onClose}
            >
                <OverlayTitleGroup
                    title={translate(advancedFilteringI18nKeys.expandedFilters.title)}
                    subTitle={translate(advancedFilteringI18nKeys.expandedFilters.subTitle)}
                />
            </OverlayHeader>

            <FilterCategoryChipList
                categoryList={categoriesWithCounts}
                selectedCategoryId={selectedCategory}
                onClick={onChangeCategory}
            />

            {belowSearchSlot}
            <Box borderBottom={`1px solid ${colorsPalettes.carbon["100"]}`} />
            <OverlayScrollableContent
                selectedCategory={selectedCategory}
                scrollToTop={selectedCategory === "all"}
            >
                <DeferredFilterContent isLoading={isLoading}>
                    <SearchResultsGuard
                        filtersManager={filtersManager}
                        isLoading={isLoading}
                    >
                        {children}
                    </SearchResultsGuard>
                </DeferredFilterContent>
            </OverlayScrollableContent>
        </ExpandedFiltersOverlay>
    );
};
